import React from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import { DateField } from "../../../../components/DateField";

// Vocabulario que escribe el backend hoy (apps/audit/constants.py). No es un
// conjunto cerrado en la base de datos, así que la tabla siempre cae al valor
// crudo si aparece uno nuevo.
const AUDIT_RESOURCES = [
  { value: "companies", label: "Academias" },
  { value: "subscriptions", label: "Suscripciones" },
  { value: "staff", label: "Staff" },
  { value: "privacy", label: "Privacidad" },
];

const AUDIT_ACTIONS = [
  { value: "create", label: "Crear" },
  { value: "update", label: "Actualizar" },
  { value: "delete", label: "Eliminar" },
  { value: "suspend", label: "Suspender" },
  { value: "reactivate", label: "Reactivar" },
  { value: "confirm_payment", label: "Confirmar pago" },
  { value: "invite", label: "Invitar staff" },
  { value: "roles_changed", label: "Cambio de roles" },
  { value: "deactivate", label: "Desactivar staff" },
  { value: "export_request", label: "Solicitud de exportación" },
  { value: "export_download", label: "Descarga de exportación" },
  { value: "deletion_request", label: "Solicitud de borrado" },
  { value: "company_offboard", label: "Baja de academia" },
  { value: "trial_expired", label: "Trial vencido" },
  { value: "downgrade_applied", label: "Downgrade aplicado" },
  { value: "subscription_frozen", label: "Suscripción congelada" },
];

const RESOURCE_LABELS = Object.fromEntries(
  AUDIT_RESOURCES.map((r) => [r.value, r.label])
);
const ACTION_LABELS = Object.fromEntries(
  AUDIT_ACTIONS.map((a) => [a.value, a.label])
);

/**
 * Filtros de la bitácora.
 *
 * `actor` es un id numérico. Cuando el usuario puede leer la lista de staff
 * (PLATFORM_ADMIN) se ofrece un desplegable; un READ_ONLY —que sí ve la
 * bitácora pero recibiría 403 en `superadmin/staff/`— cae a un campo numérico.
 * En cualquier caso la celda "Actor" de la tabla filtra con un clic, lo que
 * cubre a los actores que no son staff (p. ej. un OWNER pidiendo su export).
 */
function AuditFilters({ filters, onChange, onReset, staffOptions }) {
  return (
    <Row className="g-2 mb-3 align-items-end">
      <Col xs={12} sm={6} md={3}>
        <Form.Group controlId="audit-filter-resource">
          <Form.Label className="small mb-1">Recurso</Form.Label>
          <Form.Select
            value={filters.resource}
            onChange={(e) => onChange("resource", e.target.value)}
            aria-label="Filtrar por recurso"
            style={{ minHeight: 44 }}
          >
            <option value="">Todos</option>
            {AUDIT_RESOURCES.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>

      <Col xs={12} sm={6} md={3}>
        <Form.Group controlId="audit-filter-action">
          <Form.Label className="small mb-1">Acción</Form.Label>
          <Form.Select
            value={filters.action}
            onChange={(e) => onChange("action", e.target.value)}
            aria-label="Filtrar por acción"
            style={{ minHeight: 44 }}
          >
            <option value="">Todas</option>
            {AUDIT_ACTIONS.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>

      <Col xs={12} sm={6} md={3}>
        <Form.Group controlId="audit-filter-actor">
          <Form.Label className="small mb-1">Actor</Form.Label>
          {staffOptions?.length ? (
            <Form.Select
              value={filters.actor}
              onChange={(e) => onChange("actor", e.target.value)}
              aria-label="Filtrar por actor"
              style={{ minHeight: 44 }}
            >
              <option value="">Todos</option>
              {staffOptions.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.email})
                </option>
              ))}
            </Form.Select>
          ) : (
            <Form.Control
              type="number"
              min="1"
              inputMode="numeric"
              placeholder="ID de usuario"
              value={filters.actor}
              onChange={(e) => onChange("actor", e.target.value)}
              aria-label="Filtrar por ID del actor"
              style={{ minHeight: 44 }}
            />
          )}
        </Form.Group>
      </Col>

      <Col xs={6} sm={6} md={3}>
        <Form.Group controlId="audit-filter-from">
          <Form.Label className="small mb-1">Desde</Form.Label>
          <DateField
            id="audit-filter-from"
            value={filters.from}
            onChange={(v) => onChange("from", v)}
            max={filters.to || undefined}
          />
        </Form.Group>
      </Col>

      <Col xs={6} sm={6} md={3}>
        <Form.Group controlId="audit-filter-to">
          <Form.Label className="small mb-1">Hasta</Form.Label>
          <DateField
            id="audit-filter-to"
            value={filters.to}
            onChange={(v) => onChange("to", v)}
            min={filters.from || undefined}
          />
        </Form.Group>
      </Col>

      <Col xs={12} sm={6} md={3}>
        <Button variant="link" className="px-0" onClick={onReset}>
          Limpiar filtros
        </Button>
      </Col>
    </Row>
  );
}

export { AuditFilters, AUDIT_ACTIONS, AUDIT_RESOURCES, ACTION_LABELS, RESOURCE_LABELS };
